import { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/lib/api";
import { SITE_DEFAULTS } from "@/data/siteDefaults";

const SiteContentContext = createContext(SITE_DEFAULTS);

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const PREVIEW_KEY = "tw_preview";
// Static site assets bundled in /public/assets used when Scott has not uploaded a custom image.
const STATIC_IMAGES = {
  logoDark: "/assets/logo-dark.png",
  logoLight: "/assets/logo-light.png",
  cover: "/assets/from-campus-to-corporate-cover.png",
  scott: "/assets/scott-tang.png",
  icon: "/assets/icon.png",
};

const resolveImages = (images) => {
  const out = {};
  for (const key of Object.keys(STATIC_IMAGES)) {
    const override = images && images[key];
    out[key] = override ? `${BACKEND}${override}` : STATIC_IMAGES[key];
  }
  return out;
};

export const previewCode = {
  get: () => sessionStorage.getItem(PREVIEW_KEY) || "",
  set: (t) => sessionStorage.setItem(PREVIEW_KEY, t),
  clear: () => sessionStorage.removeItem(PREVIEW_KEY),
};

const readPreviewFromUrl = () => {
  const params = new URLSearchParams(window.location.search);
  const code = params.get("preview");
  if (code) {
    previewCode.set(code);
    params.delete("preview");
    const qs = params.toString();
    window.history.replaceState(null, "", `${window.location.pathname}${qs ? `?${qs}` : ""}${window.location.hash}`);
  }
  return previewCode.get();
};

export const SiteContentProvider = ({ children }) => {
  const [content, setContent] = useState({ ...SITE_DEFAULTS, resolvedImages: resolveImages(SITE_DEFAULTS.images), loaded: false });

  useEffect(() => {
    let active = true;
    const preview = readPreviewFromUrl();
    const fallback = setTimeout(() => { if (active) setContent((c) => (c.loaded ? c : { ...c, loaded: true })); }, 3500);
    axios.get(`${API}/content`, { params: { ...(preview ? { preview } : {}), _: Date.now() } }).then(({ data }) => {
      if (active && data) setContent({ ...data, resolvedImages: resolveImages(data.images), loaded: true });
    }).catch(() => { if (active) setContent((c) => ({ ...c, loaded: true })); });
    return () => { active = false; clearTimeout(fallback); };
  }, []);

  return <SiteContentContext.Provider value={content}>{children}</SiteContentContext.Provider>;
};

export const useSite = () => useContext(SiteContentContext);
export const useImages = () => useContext(SiteContentContext).resolvedImages;
